import { Avatar, Box, Button, Chip, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Divider,
  Fade,
  IconButton,
  Icons,
  InformationModal,
  ItemImageArea,
} from 'utoniq-ui'

import {
  ItemType,
  Maybe,
  QRcodeGetItemQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, BottomSafeArea, Head } from '@/components/layouts'
import { SupplementLinkArea } from '@/components/organisms'
import { FullscreenModal } from '@/components/organisms/functionals'
import { useMessage, User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualHiddenItemInfo } from '@/core/individual'
import { setRedirectAfterLoginPath } from '@/core/native/storage'
import { encodeSkuId } from '@/core/sku'
import { CanvasConfetti, CanvasConfettiRef } from '@/lib/canvasConfetti'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  item: Maybe<QRcodeGetItemQuery['item']>
  user: Maybe<User>
  itemId: string
  loading: boolean
  gettingItem: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onGetItem: () => Promise<void>
}

export const QRcodeGetItemTemplate: Template<State, Action> = ({
  state,
  onGetItem,
}) => {
  const { t } = useTranslation('qrCodeGetItem')
  const { handleLoginModal } = usePrivateAction()
  const [open, setOpen] = useState<boolean>(false)
  const [completion, setCompletion] = useState<boolean>(false)
  const { router, routerNames, routerPages, generatePath } = useRouter()
  const { showError } = useMessage()

  return (
    <>
      <BaseLayout
        customNavigation={
          <Box>
            <SupplementLinkArea
              pageName="QRcodeGetItem"
              itemId={state.itemId}
              artistId={state.item?.issuedArtist?.id ?? ''}
              startItems={[
                {
                  type: 'itemInfo',
                  hidden: individualHiddenItemInfo(),
                  href: generatePath(routerPages.OwnedItemInformation, {
                    itemId: state.item?.id ?? '#',
                  }),
                  onClick: handleLoginModal(),
                },
              ]}
              endItems={[
                { type: 'comment', title: state.item?.commentCount ?? 0 },
              ]}
            />
            <FixedButtonCell
              onCheck={() => setOpen(true)}
              user={state.user}
              itemId={state.itemId ?? ''}
              authenticatedAt={
                state.item?.serial?.authentication?.authenticatedAt ?? ''
              }
            />
          </Box>
        }
        header={{
          title: completion ? t('title') : '',
          left: (
            <IconButton.Close
              edge="start"
              size="medium"
              onClick={() => router.push(routerPages.GlobalHome)}
            />
          ),
          type: 'modal',
        }}
      >
        <Head title={routerNames.QRcodeGetItem} />
        {!state.loading && (
          <Fade>
            <Box>
              <ItemImageCell item={state.item} />
              <ItemDescriptionCell item={state.item} />
            </Box>
          </Fade>
        )}
        <InformationModal
          open={open}
          onClose={() => setOpen(false)}
          title={t('titleConfirm')}
          content=""
          cancelText={t('cancelTextConfirm')}
          buttons={
            <Button
              size="large"
              color="primary"
              loading={state.gettingItem}
              onClick={async () => {
                await onGetItem()
                  .then(() => {
                    setCompletion(true)
                  })
                  .catch(e => showError(e))
                  .finally(() => setOpen(false))
              }}
              sx={{ minWidth: 212 }}
            >
              {t('okTextConfirm')}
            </Button>
          }
        />
      </BaseLayout>
      <CompletionModal
        open={completion}
        onClose={() => {}}
        item={state.item}
        itemId={state.itemId}
      />
    </>
  )
}
type ItemImageCellProps = {
  item: State['item']
}
const ItemImageCell: Cell<ItemImageCellProps> = ({ item }) => {
  const { t } = useTranslation('qrCodeGetItem')

  return (
    <CellFrame
      sx={{
        display: 'grid',
        placeItems: 'center',
        pt: 2,
        pb: 5,
        gap: 5,
        px: 5,
      }}
    >
      <Stack
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <Box sx={{ mb: 5 }}>
          {item?.thumbnail?.front?.smallUri && (
            <ItemImageArea size="large" src={item.thumbnail.front.smallUri} />
          )}
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" align="center">
            {item?.title}
          </Typography>
          {item?.sku && (
            <Typography variant="body2" align="center">
              {item.sku.unitName}
            </Typography>
          )}
        </Box>
        <Stack direction="row" justifyContent="center" spacing={4}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="overline" color="text.hint">
              {t('serialNumber')}
            </Typography>
            <Chip
              label={
                <Typography variant="overline" color="text.primary">
                  {item?.serial?.id}
                </Typography>
              }
              variant="filled"
              size="small"
              sx={{
                bgcolor: 'grey.100',
                height: 16,
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type ItemDescriptionCellProps = {
  item: State['item']
}
const ItemDescriptionCell: Cell<ItemDescriptionCellProps> = ({ item }) => {
  const { t } = useTranslation('qrCodeGetItem')
  const itemIcon = useMemo(() => {
    switch (item?.type) {
      case ItemType.Audio:
        return <Icons.Audio fontSize="xSmall" />
      case ItemType.Video:
        return <Icons.Video fontSize="xSmall" />
      case ItemType.Magazine:
        return <Icons.Magazine fontSize="xSmall" />
      case ItemType.Merch:
        return <Icons.Merch fontSize="xSmall" />
      case ItemType.Wallpaper:
        return <Icons.Wallpaper fontSize="xSmall" />
      case ItemType.Ticket:
        return <Icons.Ticket fontSize="xSmall" />
      default:
        return null
    }
  }, [item?.type])

  return (
    <CellFrame sx={{ px: 5, pb: 2 }}>
      <Typography variant="body2">
        {item?.description ?? item?.metadata?.ticket?.description}
      </Typography>
      <Stack direction="row" spacing={3} alignItems="center" sx={{ pt: 4 }}>
        {item?.rarity && (
          <>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.hint">
                {t('rarity')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {item?.rarityIconImage?.smallUri && (
                  <Avatar
                    variant="square"
                    src={item.rarityIconImage.smallUri}
                    sx={{ height: 12, width: 12 }}
                  />
                )}
                <Typography variant="subtitle2">
                  {item?.rarity?.name ? t(item.rarity.name) : '-'}
                </Typography>
              </Stack>
            </Stack>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {!item?.hideSalesNumber && (
          <>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.hint">
                {t('numberOfIssues')}
              </Typography>
              <Typography variant="subtitle2">
                {item?.issuedNumber ?? '∞'}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <Stack spacing={1}>
          <Typography variant="overline" color="text.hint">
            {t('type')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {itemIcon}
            <Typography variant="subtitle2">{t(item?.type ?? '')}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type FixedButtonCellProps = {
  onCheck: () => void
  itemId: string
  user: State['user']
  authenticatedAt: string
}
const FixedButtonCell: Cell<FixedButtonCellProps> = ({
  onCheck,
  user,
  authenticatedAt,
}) => {
  const { routerPages, router } = useRouter()
  const { t } = useTranslation('qrCodeGetItem')
  const buttonText = useMemo(() => {
    if (!user) {
      return (
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
          {t('onCheckTextNotLogin')}
        </Typography>
      )
    }

    if (authenticatedAt) {
      return t('onCheckTextAcquired')
    }
    return t('onCheckTextDefault')
  }, [user, authenticatedAt, t])

  return (
    <CellFrame>
      <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
        <Stack sx={{ py: 4, bgcolor: 'grey.50' }} alignItems="center">
          <Button
            size="large"
            color="primary"
            sx={{ width: 244 }}
            onClick={async () => {
              if (!user) {
                await setRedirectAfterLoginPath(router.pathname)
                return router.push(routerPages.Top)
              }
              if (!authenticatedAt) {
                onCheck()
              }
            }}
            variant={!user ? 'text' : 'contained'}
            disabled={!!(user && authenticatedAt)}
          >
            {buttonText}
          </Button>
        </Stack>
      </BottomSafeArea>
    </CellFrame>
  )
}

type CompletionModalProps = {
  item: State['item']
  itemId: string
  open: boolean
  onClose: () => void
}
const CompletionModal: Cell<CompletionModalProps> = ({
  item,
  itemId,
  open,
  onClose,
}) => {
  const { t } = useTranslation('qrCodeGetItem')
  const { generateQueryPath, routerPages } = useRouter()
  const canvasConfettiRef = useRef<CanvasConfettiRef>(null)

  useEffect(() => {
    if (open && canvasConfettiRef.current) {
      canvasConfettiRef.current.start()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canvasConfettiRef.current])

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <BaseLayout
        header={{ title: t('completionTitle') }}
        sx={{ height: '100dvh' }}
      >
        <Stack sx={{ position: 'relative', height: '100%' }}>
          <Stack
            flex={1}
            sx={{ position: 'relative', zIndex: 10 }}
            alignItems="center"
            justifyContent="center"
          >
            <Box
              sx={{
                width: 'fit-content',
                img: {
                  boxShadow:
                    item?.rarity?.id === 'superrare' ||
                    item?.rarity?.id === 'ultrarare'
                      ? '0 0 40px yellow'
                      : 'none',
                },
              }}
            >
              <ItemImageArea
                size="large"
                src={item?.thumbnail.front?.uri ?? ''}
              />
            </Box>
          </Stack>
          <Stack sx={{ pb: 5 }} spacing={5} alignItems="center">
            <Button
              variant="outlined"
              href={generateQueryPath(
                routerPages.OwnedItemDetail,
                { itemId },
                { skuId: encodeSkuId(item?.sku?.id ?? '') }
              )}
              sx={{ position: 'relative', zIndex: 10 }}
            >
              {t('checkItem')}
            </Button>
            <Button
              variant="text"
              color="primary"
              href={routerPages.Top}
              sx={{ position: 'relative', zIndex: 10 }}
            >
              {t('goToTop')}
            </Button>
          </Stack>
          <CanvasConfetti ref={canvasConfettiRef} />
        </Stack>
      </BaseLayout>
    </FullscreenModal>
  )
}
